import { action, computed, makeObservable, observable } from 'mobx';
import { genId, RequireAllProps, StringDto } from '../../../../store/utils/Dto';
import extractValue, {
  type EventOrValue,
} from '../../../../store/utils/extractValue';
import Indicator from '../../../../store/utils/Indicator';
import Validator from '../../../../store/utils/Validator';
import { errorHandler } from '../../../../utils/errors';
import type { AccountSaveMethod } from '../types';
import type { ServiceProvider } from './types';

export default class Service<TDto = ServiceDto> {
  provider: ServiceProvider = 'amazonaws.com'; // Just to initialize mobx
  name = '';

  id = genId();

  readonly indicator = new Indicator();

  protected readonly validator = new Validator(
    this,
    (this.constructor as any).Schema,
  );

  constructor(
    dto: ServiceDto = {} as any,
    private readonly onSave?: AccountSaveMethod,
  ) {
    makeObservable(this, {
      provider: observable,
      name: observable,
      indicator: observable,
      errors: computed,
      hasErrors: computed,
      allErrorsAsStrings: computed,
      handleChange: action.bound,
      setValue: action.bound,
      handleBlur: action.bound,
      save: action.bound,
      delete: action.bound,
      toJSON: action.bound,
    });

    Object.assign(this, dto);
    this.provider = dto.provider;

    this.validator.validate();
  }

  get errors(): StringDto<TDto> {
    return this.validator.errorsForEnabledFields as any;
  }

  get hasErrors(): boolean {
    return !this.validator.isValid;
  }

  get allErrorsAsStrings(): string[] {
    return this.validator.allErrorsAsStrings;
  }

  handleChange(e: EventOrValue) {
    if (this.setValue(e)) {
      this.save().catch(errorHandler('Cannot save service config'));
    }
  }

  setValue(e: EventOrValue): boolean {
    const { name, value } = extractValue(e);

    let newValue = value;
    if (typeof newValue === 'string') {
      newValue = value.trim();
    }

    Object.assign(this, { [name]: newValue });

    return this.validator.validate();
  }

  handleBlur(e: EventOrValue<keyof this>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  async save(reason?: 'delete') {
    if (!this.onSave) {
      return;
    }

    this.indicator.spyOnPromise(
      this.onSave({
        service: this,
        reason,
      }),
    );
  }

  async delete() {
    await this.save('delete');
  }

  toJSON(): RequireAllProps<ServiceDto> {
    return {
      name: this.name,
      provider: this.provider,
    };
  }
}

export interface ServiceDto {
  [option: string]: any;
  name?: string;
  provider: ServiceProvider;
}
